import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BellRing, GraduationCap } from "lucide-react";
import { Link } from "react-router-dom";
import { Bloc, Chiffre, Barre, Fleche, pct, duree, heure, Vide } from "./commun";

export const Production = ({ p }) => {
  const o = p.objectifs;
  const lignes = [["Appels", p.appels, o.objectif_appels, "calls"], ["Décrochés", p.decroches, o.objectif_decroches, "pickups"], ["Opportunités", p.opportunites, o.objectif_opportunites, "opps"]];
  return (
    <Bloc titre="Production du jour de l'équipe" sous="Jauge par rapport à l'objectif cumulé des agents" testId="team-production">
      <div className="space-y-3">
        {lignes.map(([l, v, obj, c]) => (
          <div key={c}>
            <div className="flex justify-between text-sm"><span className="font-semibold">{l}</span><span className="tabular-nums" data-testid={`team-production-${c}`}>{v} / {obj}</span></div>
            <Barre pct={obj ? (100 * v) / obj : 0} couleur={v >= obj ? "var(--success)" : "var(--accent)"} />
          </div>
        ))}
      </div>
    </Bloc>
  );
};

export const Classement = ({ liste }) => (
  <Bloc titre="Classement des agents" sous="Opportunités sur 7 jours · flèche par rapport à la veille" testId="agents-ranking">
    {liste.length === 0 && <Vide />}
    <ol className="space-y-2">
      {liste.map((a) => (
        <li key={a.agent_id} className="at-soft p-3 flex items-center gap-3" data-testid={`ranking-row-${a.agent_id}`}>
          <span className="w-8 h-8 at-pill flex items-center justify-center font-bold shrink-0" style={{ background: a.rang === 1 ? "var(--accent)" : "var(--surface)", color: a.rang === 1 ? "var(--accent-ink)" : "var(--ink)" }}>{a.rang}</span>
          <span className="min-w-0 flex-1"><span className="font-semibold block truncate">{a.agent}</span><span className="text-xs at-muted">{a.opportunites_7j} opp. · {a.opportunites_jour} aujourd'hui · transformation {pct(a.taux_transformation)}</span></span>
          <Fleche v={a.evolution} testId={`ranking-trend-${a.agent_id}`} />
        </li>
      ))}
    </ol>
  </Bloc>
);

export const Qualite = ({ liste }) => (
  <Bloc titre="Qualité" sous="30 derniers jours : note IA des appels, validation et annulation par le confirmateur" testId="agents-quality">
    <div className="space-y-3">
      {liste.map((q) => (
        <div key={q.agent_id} className="at-soft p-3 space-y-2" data-testid={`quality-row-${q.agent_id}`}>
          <p className="font-semibold">{q.agent}</p>
          <div className="grid grid-cols-3 gap-2">
            <Chiffre label="Note moyenne" valeur={q.note_moyenne !== null ? `${String(q.note_moyenne).replace(".", ",")}/10` : "—"} testId={`quality-note-${q.agent_id}`} />
            <Chiffre label="Validation" valeur={pct(q.taux_validation)} /><Chiffre label="Annulation" valeur={pct(q.taux_annulation)} />
          </div>
          {q.motifs.length > 0 && <p className="text-xs at-muted break-words">Motifs d'annulation : {q.motifs.map((m) => `${m.motif} (${m.nombre})`).join(", ")}</p>}
          {q.formation && (
            <div className="text-xs p-2.5 at-pill-soft rounded-xl" style={{ background: "var(--surface)" }} data-testid={`training-${q.agent_id}`}>
              <p className="font-semibold flex items-center gap-1.5"><GraduationCap size={14} />Mini-formation : {q.formation.titre}</p>
              <p className="at-muted mt-0.5">{q.formation.contenu}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  </Bloc>
);

export const Alertes = ({ liste }) => (
  <Bloc titre="Alertes" testId="team-alerts">
    {liste.length === 0 ? <Vide texte="Aucune alerte." testId="team-alerts-empty" /> : (
      <div className="space-y-2">
        {liste.map((a, i) => {
          const c = <><BellRing size={15} className="shrink-0 mt-0.5" style={{ color: a.type === "interesse" ? "var(--success)" : "var(--danger)" }} /><span className="text-sm break-words">{a.texte}</span></>;
          return a.lien ? <Link key={i} to={a.lien} className="at-soft p-3 flex gap-2 min-h-[44px]" data-testid={`team-alert-${i}`}>{c}</Link>
            : <div key={i} className="at-soft p-3 flex gap-2" data-testid={`team-alert-${i}`}>{c}</div>;
        })}
      </div>
    )}
  </Bloc>
);

const ETATS = { actif: ["En activité", "var(--success)"], inactif: ["Inactif > 30 min", "var(--danger)"], absent: ["Absent", "var(--muted)"], pas_commence: ["Pas encore d'appel", "var(--temp-4)"] };

export const Presence = ({ liste, heures, meilleures }) => (
  <Bloc titre="Présence et temps au téléphone" sous={meilleures.length ? `Meilleures heures de décroché : ${meilleures.map((h) => `${h} h`).join(", ")}` : "Pas assez d'appels pour les meilleures heures"} testId="team-presence">
    <div className="space-y-2 mb-4">
      {liste.map((p) => (
        <div key={p.agent_id} className="at-soft p-3 flex items-center gap-3" data-testid={`presence-row-${p.agent_id}`}>
          <span className="w-2.5 h-2.5 at-pill shrink-0" style={{ background: ETATS[p.etat][1] }} />
          <span className="min-w-0 flex-1"><span className="font-semibold block truncate">{p.agent}</span>
            <span className="text-xs at-muted">{ETATS[p.etat][0]} · {p.appels} appel(s) · {heure(p.premier_appel)} → {heure(p.dernier_appel)}{p.retard_min ? ` · retard ${p.retard_min} min` : ""}</span></span>
          <span className="text-sm font-bold tabular-nums shrink-0" data-testid={`presence-phone-time-${p.agent_id}`}>{duree(p.temps_telephone_s)}</span>
        </div>
      ))}
    </div>
    <div className="h-40 -ml-3" data-testid="best-hours-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={heures.map((h) => ({ ...h, label: `${h.heure}h` }))}>
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--muted)" }} interval={1} />
          <YAxis tick={{ fontSize: 10, fill: "var(--muted)" }} width={28} unit="%" />
          <Tooltip contentStyle={{ background: "var(--surface)", border: "none", borderRadius: 12, color: "var(--ink)" }} cursor={{ fill: "var(--surface-2)" }} formatter={(v) => [`${v} %`, "Décroché"]} />
          <Bar dataKey="taux" fill="var(--temp-6)" radius={[5, 5, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Bloc>
);
